import { useEffect, useMemo, useRef, useState, type FC, type Dispatch } from 'react';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { AnimalDefinitionMappingV1 } from '@/model/PlannedAnimal.model';
import type { RandomizeState } from './Randomize/Randomize.model';
import type { StepFormAction } from '@/components/UI/StepForm/StepForm.model';
import type { ModalProps } from '../Show';
import type { PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { animalsWithSignificantMeasurement } from './Randomize/Randomize.utils';
import Banner from '@/components/UI/Banner';
import Switch from '@/components/UI/FormElements/Switch';
import {
  type MapAnimalRow,
  transformAnimalsToMapData,
  createAnimalMappings,
} from '@/components/Animals/MapAnimals.utils';
import MapRandomizedAnimalsTable from './MapRandomizedAnimalsTable';

interface MapRandomisedAnimalsProps {
  state: RandomizeState;
  dispatch: Dispatch<StepFormAction<RandomizeState>>;
  props: ModalProps;
  plannedAnimals: Array<PlannedAnimalDefinition>;
}

const MapRandomizedAnimals: FC<MapRandomisedAnimalsProps> = ({ state, dispatch, props, plannedAnimals }) => {
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [mapData, setMapData] = useState<Array<MapAnimalRow>>([]);
  const [overrideNames, setOverrideNames] = useState<boolean>(false);

  const tableRef = useRef<HotTableRef>(null);
  const { groups } = props;
  const {
    randomize,
    randomizeByDate: { measurementsOnDate },
    exclusionCriteria,
  } = state;

  const saveMappings = (data: Array<MapAnimalRow>, overrideNamesFlag: boolean) => {
    const mappedAnimals: Array<AnimalDefinitionMappingV1> = createAnimalMappings(data);
    dispatch({
      type: 'update',
      id: 'mapAnimals',
      data: {
        mappings: mappedAnimals,
        override_names: overrideNamesFlag,
      },
    });
  };

  const handleDataChange = (updatedData: Array<MapAnimalRow>) => {
    saveMappings(updatedData, overrideNames);
  };

  const handleValidation = (invalid: boolean) => {
    setIsInvalid(invalid);
    if (invalid) {
      dispatch({ type: 'finalStepReady', data: false });
    } else {
      dispatch({ type: 'finalStepReady' });
    }
  };

  useEffect(() => {
    dispatch({ type: 'stepReady' });
  }, [dispatch]);

  const randomizedAnimals = useMemo(() => {
    if (_isEmpty(randomize.results)) {
      return [];
    }

    const subjectsAfterExclusion = (exclusionCriteria?.subjectsAfterExclusion ?? []).filter((animal) =>
      _isNil(animal.animal_definition_api_id)
    );
    const animalsWithMeasurements = animalsWithSignificantMeasurement(subjectsAfterExclusion, measurementsOnDate);

    const resultAnimals: Array<Animal> = [];
    randomize.results.forEach((result) => {
      if (_notNil(result.cohort_subject_ids)) {
        result.cohort_subject_ids.forEach((subjectId) => {
          const animal = animalsWithMeasurements.find((a) => a.id === subjectId);
          if (_notNil(animal)) {
            resultAnimals.push({
              ...animal,
              study_group: groups?.find((g) => g.api_id === result.api_id) ?? animal.study_group,
            });
          }
        });
      }
    });

    return resultAnimals;
  }, [randomize.results, exclusionCriteria.subjectsAfterExclusion, measurementsOnDate, groups]);

  useEffect(() => {
    if (_isNotEmpty(randomizedAnimals) && _isNotEmpty(plannedAnimals)) {
      const transformedData = transformAnimalsToMapData(randomizedAnimals, plannedAnimals);
      setMapData(transformedData);
      saveMappings(transformedData, overrideNames);
    }
  }, [randomizedAnimals, plannedAnimals]);

  if (_isEmpty(randomizedAnimals)) {
    return (
      <div className="pa4">
        <Banner warning dismiss={false}>
          <h3 className="f5 normal lh-title pb2" data-testid={'empty-state-banner'}>
            No valid mappings found
          </h3>
          <p className="f6 pb3 lh-copy">
            There are no available randomized animals which are not already mapped to planned animals
          </p>
        </Banner>
      </div>
    );
  }

  return (
    <div className="pa4">
      <div className="mb4">
        <Banner info dismiss={false} className="mw6 mb4">
          <p className="f6 lh-copy pb3">
            This is an optional step. Planned animals can be mapped to randomized by selecting an available planned
            animal ID. Animals are pre-filtered to match group and sex.
          </p>
        </Banner>
      </div>
      <div className="flex justify-between items-center mb3">
        <div className="flex items-center">
          <Switch
            testId="overrideNames"
            value={overrideNames}
            onChange={() => {
              setOverrideNames((prev) => {
                return !prev;
              });
              saveMappings(mapData, !overrideNames);
            }}
          />
          <label className="ml2 mb0">Overwrite animal names with planned IDs</label>
        </div>
      </div>
      <div
        className="ow-spreadsheet-styles"
        data-test-component="MapRandomizedAnimals"
        data-test-element="spreadsheet-container"
      >
        <MapRandomizedAnimalsTable
          mapData={mapData}
          setMapData={setMapData}
          groups={groups}
          plannedAnimals={plannedAnimals}
          innerRef={tableRef}
          setInvalid={handleValidation}
          onDataChange={handleDataChange}
        />
      </div>
      {isInvalid && <p className="f6 red mt2">Please resolve all validation errors before proceeding.</p>}
    </div>
  );
};

export default MapRandomizedAnimals;
